import React, { useState } from 'react';
import { Bug, ChevronDown, ChevronUp, Shield, ShieldAlert } from 'lucide-react';
import { useAbilityContext } from '../../contexts/AbilityContext';
import { useCurrentUser, useCurrentUserPermissions } from '../../services/queries/permissionQueries';
import { getAppViewRole } from '../../services/rbac/rolePermissionSummary';
import { shouldBypassRoleChecks } from '../../config/mvpConfig';

const PANEL_COLLAPSED_KEY = 'regal_rbac_debug_panel_collapsed';

function isDebugEnabled(): boolean {
  if (process.env.NODE_ENV === 'development') return true;
  if (typeof window === 'undefined') return false;
  return localStorage.getItem('regal_rbac_debug') === 'true';
}

function readInitialExpanded(): boolean {
  if (typeof window === 'undefined') return true;
  try {
    return localStorage.getItem(PANEL_COLLAPSED_KEY) !== 'true';
  } catch {
    return true;
  }
}

export const PermissionDebugPanel: React.FC = () => {
  const { data: currentUser } = useCurrentUser();
  const { data: permissions } = useCurrentUserPermissions();
  const { ability, roles, role, isLoading } = useAbilityContext();
  const [expanded, setExpanded] = useState(readInitialExpanded);

  if (!isDebugEnabled()) return null;

  const roleNames = roles.map((item) => item.name);
  const keycloakRoles = currentUser?.keycloakRoles ?? [];
  const noRoleAssigned = !isLoading && roles.length === 0;

  const toggleExpanded = () => {
    setExpanded((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(PANEL_COLLAPSED_KEY, next ? 'false' : 'true');
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  return (
    <div className="fixed bottom-4 right-4 z-[70] w-[360px] max-w-[calc(100vw-2rem)] rounded-xl border border-gray-300 dark:border-gray-700 bg-white/95 dark:bg-gray-900/95 shadow-2xl backdrop-blur">
      <button
        type="button"
        onClick={toggleExpanded}
        className="flex w-full items-center justify-between gap-2 border-b border-gray-200 dark:border-gray-800 px-4 py-3 text-left transition-colors hover:bg-gray-50/80 dark:hover:bg-gray-800/60"
        aria-expanded={expanded}
        aria-controls="rbac-debug-panel-body"
        id="rbac-debug-panel-header"
      >
        <div className="flex min-w-0 flex-1 items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
          <Bug className="h-4 w-4 shrink-0 text-purple-600 dark:text-purple-400" />
          <span className="truncate">RBAC Debug</span>
          {!expanded ? (
            <span className="truncate font-normal text-gray-500 dark:text-gray-400">
              · {roleNames.length > 0 ? `${roleNames.length} role(s)` : 'no roles'}
            </span>
          ) : null}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {expanded ? (
            <span className="hidden text-[10px] text-gray-500 dark:text-gray-400 sm:inline">
              `regal_rbac_debug`
            </span>
          ) : null}
          {expanded ? (
            <ChevronUp className="h-4 w-4 text-gray-500 dark:text-gray-400" aria-hidden />
          ) : (
            <ChevronDown className="h-4 w-4 text-gray-500 dark:text-gray-400" aria-hidden />
          )}
        </div>
      </button>

      {expanded ? (
        <div className="border-b border-gray-100 px-4 pb-2 pt-2 text-xs text-gray-500 dark:border-gray-800 dark:text-gray-400">
          Show this panel:{' '}
          <code className="rounded bg-gray-100 px-1 font-mono text-[10px] dark:bg-gray-800">
            {`localStorage.setItem('regal_rbac_debug', 'true')`}
          </code>
        </div>
      ) : null}

      {expanded ? (
        <div
          id="rbac-debug-panel-body"
          role="region"
          aria-labelledby="rbac-debug-panel-header"
          className="space-y-3 px-4 py-3 text-xs"
        >
        {noRoleAssigned && shouldBypassRoleChecks() && (
          <div className="flex items-start gap-2 rounded-lg border border-blue-300 bg-blue-50 px-3 py-2 text-blue-900 dark:border-blue-800 dark:bg-blue-950/40 dark:text-blue-200">
            <Shield className="mt-0.5 h-4 w-4 shrink-0" />
            <div>
              <div className="font-semibold">No Gobodo role assigned</div>
              <div>RBAC bypass is on — CASL uses full ability. Assign Gobodo roles to test production RBAC.</div>
            </div>
          </div>
        )}
        {noRoleAssigned && !shouldBypassRoleChecks() && (
          <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-amber-900 dark:border-amber-800 dark:bg-amber-950/50 dark:text-amber-200">
            <ShieldAlert className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <div>
              <div className="font-semibold">No Gobodo role assigned</div>
              <div>CASL is running with minimal ability. Assign at least one Gobodo role to validate real RBAC.</div>
            </div>
          </div>
        )}

        <section>
          <div className="mb-1 font-semibold text-gray-700 dark:text-gray-200">Resolved roles</div>
          <div className="text-gray-600 dark:text-gray-300">
            {roleNames.length > 0 ? roleNames.join(', ') : 'None'}
          </div>
          <div className="mt-1 text-gray-500 dark:text-gray-400">
            Primary: {role?.name ?? 'None'}
          </div>
        </section>

        <section>
          <div className="mb-1 font-semibold text-gray-700 dark:text-gray-200">Keycloak roles</div>
          <div className="text-gray-600 dark:text-gray-300 break-words">
            {keycloakRoles.length > 0 ? keycloakRoles.join(', ') : 'None'}
          </div>
        </section>

        <section>
          <div className="mb-1 font-semibold text-gray-700 dark:text-gray-200">Accessible apps</div>
          <div className="text-gray-600 dark:text-gray-300">
            {permissions?.accessibleApps?.join(', ') ?? 'regal_main'}
          </div>
        </section>

        <section className="grid grid-cols-2 gap-2">
          <div className="rounded-lg bg-gray-50 px-3 py-2 dark:bg-gray-800">
            <div className="font-semibold text-gray-700 dark:text-gray-200">CyberSweep view</div>
            <div className="text-gray-600 dark:text-gray-300">{getAppViewRole('cybersweep', roles, keycloakRoles)}</div>
          </div>
          <div className="rounded-lg bg-gray-50 px-3 py-2 dark:bg-gray-800">
            <div className="font-semibold text-gray-700 dark:text-gray-200">Credit Block view</div>
            <div className="text-gray-600 dark:text-gray-300">{getAppViewRole('credit_block', roles, keycloakRoles)}</div>
          </div>
        </section>

        <section className="grid grid-cols-2 gap-2">
          <div className="rounded-lg bg-gray-50 px-3 py-2 dark:bg-gray-800">
            <div className="font-semibold text-gray-700 dark:text-gray-200">Can access CB</div>
            <div className="text-gray-600 dark:text-gray-300">{String(ability.can('access', 'credit_block'))}</div>
          </div>
          <div className="rounded-lg bg-gray-50 px-3 py-2 dark:bg-gray-800">
            <div className="font-semibold text-gray-700 dark:text-gray-200">Can access CS</div>
            <div className="text-gray-600 dark:text-gray-300">{String(ability.can('access', 'cybersweep'))}</div>
          </div>
        </section>

        <section className="flex items-start gap-2 text-gray-500 dark:text-gray-400">
          <Shield className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            Collapsed state persists after refresh:{' '}
          <code className="rounded bg-gray-100 px-1 font-mono text-[10px] dark:bg-gray-800">
            {`localStorage.setItem('${PANEL_COLLAPSED_KEY}', 'true')`}
          </code>
          </span>
        </section>
        </div>
      ) : null}
    </div>
  );
};

export default PermissionDebugPanel;
